import axios from "./axios";
import {
  LocationCreateUpdateDto,
  LocationResponseDto,
} from "../components/common/ObjectTypes";
import { LocationSearchQueryType } from "../components/common/QueryTypes";

export async function getLocations(
  query: LocationSearchQueryType,
): Promise<{ locations: LocationResponseDto[]; count: number }> {
  try {
    const resp = await axios.get("/locations", {
      params: query,
    });
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function getLocation(
  locationId: number,
): Promise<LocationResponseDto> {
  try {
    const resp = await axios.get(`/locations/${locationId}`);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function createLocation(
  data: LocationCreateUpdateDto,
): Promise<LocationResponseDto> {
  try {
    const resp = await axios.post("/locations", data);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function updateLocation(
  data: LocationCreateUpdateDto,
): Promise<LocationResponseDto> {
  try {
    const resp = await axios.put(`/locations/${data.location_id}`, data);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function deleteLocation(locationId: number): Promise<any> {
  try {
    const resp = await axios.delete(`/locations/${locationId}`);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

const GEOCODER_URL = "https://nominatim.openstreetmap.org/reverse";
const GEOCODER_TIMEOUT_MS = 10000;

/**
 * Returns the country code of the location at the given coordinates or null if it could not be determined, using OpenStreetMap.
 *
 * This is a call to a third party, so it deliberately does **not** use the shared axios
 * instance: that instance adds the user's Keycloak token to every request and applies the API's
 * 401/5xx handling (sign-out, redirect to /unexpectedError) to every answer (AKL-1110). A bare
 * `fetch` carries no credential and no cookies, and any failure, including a non-2xx answer,
 * comes back as null.
 * @param latitude the latitude of the location in decimal degrees
 * @param longitude the longitude of the location in decimal degrees
 * @returns the country code of the location, or null if it could not be determined
 */
export const getCountryCodeFromCoords = async (
  latitude: number,
  longitude: number,
): Promise<string | null> => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), GEOCODER_TIMEOUT_MS);
  try {
    const query = new URLSearchParams({
      format: "json",
      lat: String(latitude),
      lon: String(longitude),
      zoom: "10",
    });
    const resp = await fetch(`${GEOCODER_URL}?${query}`, {
      credentials: "omit",
      signal: controller.signal,
    });
    if (!resp.ok) {
      throw new Error(`The geocoder answered ${resp.status}`);
    }
    const data = await resp.json();
    return data?.address?.country_code || null;
  } catch (error) {
    console.error(
      `Geocoding failed for location ${latitude}, ${longitude} :`,
      error,
    );
    return null;
  } finally {
    clearTimeout(timer);
  }
};
